/**
 * Structured Logger for Frontend
 *
 * Provides consistent JSON logging format matching the backend.
 * Supports log levels, context binding, and structured data.
 */

// Log levels with numeric priority
const LOG_LEVELS = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
}

// Current log level from environment or default to 'info'
const CURRENT_LEVEL = LOG_LEVELS[import.meta.env.VITE_LOG_LEVEL] || LOG_LEVELS.info

// App name for log context
const APP_NAME = 'surveytip-frontend'

/**
 * Format a log entry as structured JSON
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {Object} context - Additional context data
 * @returns {Object} Structured log object
 */
function formatLogEntry(level, message, context = {}) {
  return {
    timestamp: new Date().toISOString(),
    level: level.toUpperCase(),
    app: APP_NAME,
    message,
    ...context,
    // Add browser context in non-production
    ...(import.meta.env.DEV && {
      url: window.location.pathname,
      userAgent: navigator.userAgent.substring(0, 100),
    }),
  }
}

/**
 * Output log entry to console
 * @param {string} level - Log level
 * @param {Object} entry - Structured log entry
 */
function outputLog(level, entry) {
  const jsonString = JSON.stringify(entry)

  switch (level) {
    case 'error':
      console.error(jsonString)
      break
    case 'warn':
      console.warn(jsonString)
      break
    case 'debug':
      console.debug(jsonString)
      break
    default:
      console.log(jsonString)
  }
}

/**
 * Check if log level should be output
 * @param {string} level - Log level to check
 * @returns {boolean}
 */
function shouldLog(level) {
  return LOG_LEVELS[level] >= CURRENT_LEVEL
}

/**
 * Create a logger instance with optional bound context
 * @param {string} name - Logger name (usually component/module name)
 * @param {Object} boundContext - Context to include in all logs
 * @returns {Object} Logger instance
 */
export function createLogger(name, boundContext = {}) {
  const baseContext = { logger: name, ...boundContext }

  return {
    debug: (message, context = {}) => {
      if (shouldLog('debug')) {
        outputLog('debug', formatLogEntry('debug', message, { ...baseContext, ...context }))
      }
    },

    info: (message, context = {}) => {
      if (shouldLog('info')) {
        outputLog('info', formatLogEntry('info', message, { ...baseContext, ...context }))
      }
    },

    warn: (message, context = {}) => {
      if (shouldLog('warn')) {
        outputLog('warn', formatLogEntry('warn', message, { ...baseContext, ...context }))
      }
    },

    error: (message, context = {}) => {
      if (shouldLog('error')) {
        // Extract error details if an Error object is provided
        if (context.error instanceof Error) {
          context = {
            ...context,
            error: {
              name: context.error.name,
              message: context.error.message,
              stack: import.meta.env.DEV ? context.error.stack : undefined,
            },
          }
        }
        outputLog('error', formatLogEntry('error', message, { ...baseContext, ...context }))
      }
    },

    /**
     * Create a child logger with additional bound context
     * @param {Object} childContext - Additional context for child logger
     * @returns {Object} Child logger instance
     */
    child: (childContext) => {
      return createLogger(name, { ...baseContext, ...childContext })
    },
  }
}

/**
 * Default logger instance
 */
export const logger = createLogger('app')

/**
 * Log an API request (for debugging)
 * @param {string} method - HTTP method
 * @param {string} endpoint - API endpoint
 * @param {number} status - Response status
 * @param {number} durationMs - Request duration in ms
 */
export function logApiRequest(method, endpoint, status, durationMs) {
  const apiLogger = createLogger('api')

  const context = {
    method,
    endpoint,
    status,
    duration_ms: durationMs,
  }

  if (status >= 400) {
    apiLogger.error(`${method} ${endpoint} failed`, context)
  } else {
    apiLogger.debug(`${method} ${endpoint}`, context)
  }
}

/**
 * Log a user action (for analytics/debugging)
 * @param {string} action - Action name
 * @param {Object} data - Action data
 */
export function logUserAction(action, data = {}) {
  const actionLogger = createLogger('user-action')
  actionLogger.info(action, data)
}

export default logger
